import React, { useState, useEffect } from "react";
import { Container, Col, Row, Button, Modal, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import axios from "axios";

import NavMain from "../components/nav/NavMain";
import HeaderLoan from "../components/header/HeaderLoan";
import EditLoan from "../components/modals/EditLoan";
import DueDates from "../components/misc/DueDates";
import ToPeso from "../components/misc/ToPeso";
import LoanFiles from '../components/files/LoanFiles';
import GLOBALVAR from "../constants";

import '../css/loan-page.scss';

function LoanPage() {
    const { id } = useParams();
    const [loan, setLoan] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [showModalDeletePayment, setShowModalDeletePayment] = useState(false);
    const handleClose = () => setShowModal(false);
    const handleShow = () => setShowModal(true);
    const handleCloseDelete = () => setShowModalDelete(false);
    const handleShowDelete = () => setShowModalDelete(true);
    const handleCloseDeletePayment = () => setShowModalDeletePayment(false);
    const handleShowDeletePayment = () => setShowModalDeletePayment(true);
    const navigate = useNavigate();
    const [paymentList, setPaymentList] = useState(null);
    const [payment, setPayment] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [penaltyData, setPenaltyData] = useState(null);

    const fetchLoan = async (id) => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/${id}`);
            const loanData = response.data;
            setPaymentList(loanData.payments);
            setLoan(loanData);
            return loanData;
        } catch (error) {
            console.error('Error fetching loan:', error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPenalties = async (id, payments = []) => {
        try {
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/penalties/loan/${id}`);
            
            // Process the response to calculate consolidated values
            if (response.data && response.data.data) {
                const penaltyRecords = response.data.data;
                
                // Step 1: Group ALL penalties by lastPaymentDate (use latest per date to avoid double counting recalculations)
                const groupedByDate = {};
                penaltyRecords.forEach(penalty => {
                    const paymentDate = penalty.lastPaymentDate || 'unknown';
                    if (!groupedByDate[paymentDate]) {
                        groupedByDate[paymentDate] = [];
                    }
                    groupedByDate[paymentDate].push(penalty);
                });
                
                // Step 2: Get only the latest record for each lastPaymentDate group
                const latestRecordsPerDate = Object.values(groupedByDate).map(group => {
                    // Sort by createdAt or calculationDate (newest first) and take the first item
                    return group.sort((a, b) => {
                        const dateA = new Date(a.createdAt || a.calculationDate);
                        const dateB = new Date(b.createdAt || b.calculationDate);
                        return dateB - dateA;
                    })[0];
                });
                
                // Step 3: Virtually apply paid penalties from payments to filtered records by payment date (oldest first)
                const adjustedRecords = latestRecordsPerDate
                    .map(rec => ({
                        ...rec,
                        _calcDate: new Date(rec.calculationDate || rec.createdAt || rec.lastPaymentDate || Date.now()),
                        _penaltyAmount: parseFloat(rec.penaltyAmount || 0),
                        _paidAmount: parseFloat(rec.paidAmount || 0),
                        _adjustedPaid: parseFloat(rec.paidAmount || 0),
                    }))
                    .sort((a, b) => a._calcDate - b._calcDate);

                // Build payment penalty events
                const penaltyPayments = (payments || [])
                    .filter(p => parseFloat(p.paidPenalty || 0) > 0)
                    .map(p => ({
                        date: new Date(p.paymentDate),
                        amount: parseFloat(p.paidPenalty)
                    }))
                    .sort((a, b) => a.date - b.date);

                // Apply each payment to penalties accrued up to that payment date
                for (const pay of penaltyPayments) {
                    let remaining = pay.amount;
                    for (const rec of adjustedRecords) {
                        if (remaining <= 0) break;
                        // Only apply to penalties calculated on or before the payment date
                        if (rec._calcDate <= pay.date) {
                            const remainingOnRec = Math.max(0, rec._penaltyAmount - rec._adjustedPaid);
                            if (remainingOnRec > 0) {
                                const applied = Math.min(remaining, remainingOnRec);
                                rec._adjustedPaid += applied;
                                remaining -= applied;
                            }
                        }
                    }
                }

                // Totals after virtual application (totals now reflect ALL penalties, paid and unpaid)
                const filteredTotalPenaltyAmount = adjustedRecords.reduce((sum, r) => sum + r._penaltyAmount, 0);
                const filteredTotalPaidAmount = adjustedRecords.reduce((sum, r) => sum + r._adjustedPaid, 0);
                const outstandingBalance = Math.max(0, filteredTotalPenaltyAmount - filteredTotalPaidAmount);
                const totalUnpaidAmount = adjustedRecords.reduce((sum, r) => sum + Math.max(0, r._penaltyAmount - r._adjustedPaid), 0);
                
                // Enhance the response with additional calculated values
                const enhancedData = {
                    ...response.data,
                    consolidatedData: {
                        unpaidCount: adjustedRecords.filter(r => (r._penaltyAmount - r._adjustedPaid) > 0.01).length,
                        totalUnpaidAmount: totalUnpaidAmount.toFixed(2),
                        outstandingBalance: outstandingBalance.toFixed(2),
                        filteredTotalPenaltyAmount: filteredTotalPenaltyAmount.toFixed(2),
                        filteredTotalPaidAmount: filteredTotalPaidAmount.toFixed(2),
                        oldestPenaltyDate: latestRecordsPerDate.length > 0 ? 
                            Math.min(...latestRecordsPerDate.map(p => new Date(p.calculationDate).getTime())) : null,
                        filteredRecords: adjustedRecords.map(({ _calcDate, _penaltyAmount, _adjustedPaid, ...rest }) => ({
                            ...rest,
                            penaltyAmount: (_penaltyAmount || 0),
                            paidAmount: (_adjustedPaid || 0)
                        }))
                    }
                };
                
                setPenaltyData(enhancedData);
            } else {
                setPenaltyData(response.data);
            }
        } catch (error) {
            console.error('Error fetching penalties:', error);
        }
    };

    const handleDeleteProcess = async(id) => {
        if (!id) {
            return;
        }
        
        try {
            await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/delete/${id}`);
            navigate('/');
        } catch (error) {
            console.error('Error deleting loan:', error);
        }
    };

    const handleDeleteProcessPayment = async(id) => {
        if (!payment) {
            return;
        }
        
        try {
            await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/payments/delete/${payment}`);
            window.location.reload();
        } catch (error) {
            console.error('Error deleting payment:', error);
        }
    };

    const updateParentLoan = (newLoanData) => {
        setLoan(newLoanData);
    };

    useEffect(() => {
        const load = async () => {
            const loanData = await fetchLoan(id);
            const payments = loanData?.payments || [];
            await fetchPenalties(id, payments);
        };
        load();
    }, [id]);

    return (
        <>
            {/* Modals */}
            <Modal show={showModal} onHide={handleClose} className="edit-loan-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Loan Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <EditLoan loanProcessingData={loan} updateParentLoanProcessing={updateParentLoan} />
                </Modal.Body>
            </Modal>

            {/* Delete Loan Modal */}
            <Modal show={showModalDelete} onHide={handleCloseDelete}>
                <Modal.Header closeButton>
                    <Modal.Title className="h6">Are you sure you want to delete this loan?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-center">
                        <Button variant="primary" className="me-2" onClick={handleCloseDelete}>No</Button>
                        <Button variant="warning" onClick={() => handleDeleteProcess(loan?.id)}>Yes</Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Delete Payment Modal */}
            <Modal show={showModalDeletePayment} onHide={handleCloseDeletePayment} className="delete-loan-modal">
                <Modal.Header closeButton>
                    <Modal.Title className="h6">Are you sure you want to delete this payment?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-center">
                        <Button variant="primary" className="me-2" onClick={handleCloseDeletePayment}>No</Button>
                        <Button variant="warning" onClick={() => handleDeleteProcessPayment(payment)}>Yes</Button>
                    </div>
                </Modal.Body>
            </Modal>

            <NavMain />
            <HeaderLoan loan={loan} />
            
            <div className="main-content loan-page">
                {isLoading ? (
                    <div className="loader-container d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" role="status" variant="primary" />
                            <p className="mt-2 text-muted">Loading loan details...</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="quick-links">
                            <Button variant="primary" onClick={handleShow}>
                                <i className="fa-solid fa-pencil"></i> Edit Details
                            </Button>
                            <Button variant="warning" onClick={handleShowDelete}>
                                <i className="fa-solid fa-trash"></i> Send to Trash
                            </Button>
                            <Button variant="primary" href={`/add-repayment/?id=${id}`}>
                                <i className="fa-solid fa-credit-card"></i> Add Payment
                            </Button>
                            <Button variant="primary" href={`/applicant/${loan?.applicant.id}`}>
                                <i className="fa-regular fa-circle-user"></i> Applicant Page
                            </Button>
                            <Button 
                                variant="info" 
                                href={`/loan/${new Date(loan?.dateGranted).getTime()}/${loan?.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <i className="fa-solid fa-external-link"></i> Public View
                            </Button>
                        </div>

                        <Container className="parent-container">
                            <Row>
                                <Col lg={5} className="loan-details">
                                    {loan && (
                                        <>
                                            <div className="details-section basic-info">
                                                <h5 className="section-title">
                                                    <i className="fa-solid fa-info-circle"></i>
                                                    Basic Information
                                                </h5>
                                                <Row>
                                                    <Col md={4} className="title">Loan Processing Type</Col>
                                                    <Col md={8} className="value">{loan.loanProcessingType}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Loan Type</Col>
                                                    <Col md={8} className="value">{loan.loanType}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Loan Product</Col>
                                                    <Col md={8} className="value">{loan.loanProduct}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Loan Security</Col>
                                                    <Col md={8} className="value">{loan.loanSecurity}</Col>
                                                </Row>
                                            </div>

                                            <div className="details-section financial-info">
                                                <h5 className="section-title">
                                                    <i className="fa-solid fa-money-bill-wave"></i>
                                                    Financial Details
                                                </h5>
                                                <Row>
                                                    <Col md={4} className="title">Amount Granted</Col>
                                                    <Col md={8} className="value"><ToPeso amount={loan.amountGranted} /></Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Net Amount</Col>
                                                    <Col md={8} className="value">
                                                        {loan.advanceInterest ? (
                                                            <ToPeso amount={
                                                                loan.amountGranted - (
                                                                    loan.serviceFee + 
                                                                    loan.notarialFee + 
                                                                    loan.docStamp + 
                                                                    loan.insurance + 
                                                                    (loan.amountGranted * (loan.interestRate / 100))
                                                                )
                                                            } />
                                                        ) : (
                                                            <ToPeso amount={loan.amountGranted} />
                                                        )}
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Interest Rate</Col>
                                                    <Col md={8} className="value">{loan.interestRate}%</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Loan Term</Col>
                                                    <Col md={8} className="value">{loan.loanTerm}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Date Granted</Col>
                                                    <Col md={8} className="value">{format(new Date(loan.dateGranted), 'MMM dd, yyyy')}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Payment Interval</Col>
                                                    <Col md={8} className="value">{loan.loanInterval}</Col>
                                                </Row>
                                            </div>

                                            <div className="details-section fees-info">
                                                <h5 className="section-title">
                                                    <i className="fa-solid fa-file-invoice-dollar"></i>
                                                    Fees & Charges
                                                </h5>
                                                <Row>
                                                    <Col md={4} className="title">Service Fee</Col>
                                                    <Col md={8} className="value">
                                                        {loan.advanceInterest && <ToPeso amount={loan.serviceFee} />}
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Notarial Fee</Col>
                                                    <Col md={8} className="value">
                                                        {loan.advanceInterest && <ToPeso amount={loan.notarialFee} />}
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Doc Stamp</Col>
                                                    <Col md={8} className="value">
                                                        {loan.advanceInterest && <ToPeso amount={loan.docStamp} />}
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Insurance</Col>
                                                    <Col md={8} className="value">
                                                        {loan.advanceInterest && <ToPeso amount={loan.insurance} />}
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Advance Interest</Col>
                                                    <Col md={8} className="value">{loan.advanceInterest ? "ADDED" : "NOT ADDED"}</Col>
                                                </Row>
                                            </div>

                                            <div className="details-section additional-info">
                                                <h5 className="section-title">
                                                    <i className="fa-solid fa-circle-info"></i>
                                                    Additional Information
                                                </h5>
                                                <Row>
                                                    <Col md={4} className="title">Account Officer</Col>
                                                    <Col md={8} className="value">{loan.accountOfficer}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Loan Ledger</Col>
                                                    <Col md={8} className="value">{loan.loanLedger}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Reference</Col>
                                                    <Col md={8} className="value">{loan.reference}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Beneficiary</Col>
                                                    <Col md={8} className="value">{loan.addtl_beneficiary}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Contact</Col>
                                                    <Col md={8} className="value">{loan.addtl_contact}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4} className="title">Relationship</Col>
                                                    <Col md={8} className="value">{loan.addtl_relationship}</Col>
                                                </Row>
                                            </div>

                                            <div className="payment-section loan-files">
                                                <LoanFiles loanId={id} />
                                            </div>
                                        </>
                                    )}
                                </Col>

                                <Col lg={6}>
                                    <div className="payment-section payment-duedates">
                                        {loan && (
                                            <>
                                                <h5 className="section-title">
                                                    <i className="fa-solid fa-calendar-days"></i>
                                                    Due Dates ({loan.loanInterval})
                                                </h5>
                                                <DueDates 
                                                    date={loan.dateGranted} 
                                                    interval={loan.loanInterval} 
                                                    term={loan.loanTerm} 
                                                    amount={loan.amountGranted} 
                                                    interest={loan.interestRate} 
                                                    paymentList={paymentList} 
                                                    loanItem={loan} 
                                                />
                                            </>
                                        )}
                                    </div>

                                    <div className="payment-section penalty-history">
                                        <h5 className="section-title">
                                            <i className="fa-solid fa-triangle-exclamation"></i>
                                            Penalty Records
                                        </h5>
                                        {penaltyData && penaltyData.data?.length > 0 ? (
                                            <div className="penalty-consolidated-view">
                                                <div className="penalty-summary">
                                                    <div className="summary-item">
                                                        <span className="label">Total Penalties:</span>
                                                        <span className="value">
                                                            {penaltyData.consolidatedData?.filteredTotalPenaltyAmount ? 
                                                                <ToPeso amount={penaltyData.consolidatedData.filteredTotalPenaltyAmount} /> : 
                                                                <ToPeso amount={penaltyData.summary.totalPenaltyAmount} />
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="summary-item">
                                                        <span className="label">Total Penalties Paid:</span>
                                                        <span className="value">
                                                            {penaltyData.consolidatedData?.filteredTotalPaidAmount ? 
                                                                <ToPeso amount={penaltyData.consolidatedData.filteredTotalPaidAmount} /> : 
                                                                <ToPeso amount={penaltyData.summary.totalPaidAmount} />
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="summary-item">
                                                        <span className="label">Outstanding Penalties:</span>
                                                        <span className="value">
                                                            {penaltyData.consolidatedData?.outstandingBalance ? 
                                                                <ToPeso amount={penaltyData.consolidatedData.outstandingBalance} /> : 
                                                                <ToPeso amount={
                                                                    parseFloat(penaltyData.summary.totalPenaltyAmount) - 
                                                                    parseFloat(penaltyData.summary.totalPaidAmount)
                                                                } />
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                <div className="penalty-details">
                                                    <div className="detail-grid">
                                                        <div className="detail-item">
                                                            <span className="label">Total Records:</span>
                                                            <span className="value">{penaltyData.count}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="label">Paid Records:</span>
                                                            <span className="value">{penaltyData.summary.paidPenalties}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="label">Unpaid Records:</span>
                                                            <span className="value">{penaltyData.count - penaltyData.summary.paidPenalties}</span>
                                                        </div>
                                                        {penaltyData.consolidatedData?.unpaidCount > 0 && (
                                                            <div className="detail-item">
                                                                <span className="label">Oldest Unpaid Penalty:</span>
                                                                <span className="value">
                                                                    {penaltyData.consolidatedData.oldestPenaltyDate ? 
                                                                        format(new Date(penaltyData.consolidatedData.oldestPenaltyDate), 'MMM dd, yyyy') : 
                                                                        'N/A'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="no-payments-message">
                                                <div className="message-content">
                                                    <i className="fa-solid fa-check-circle"></i>
                                                    <h6>No Penalties</h6>
                                                    <p>This loan has no penalty records.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="payment-section payment-history">
                                        <h5 className="section-title">
                                            <i className="fa-solid fa-clock-rotate-left"></i>
                                            Payment Records
                                        </h5>
                                        {paymentList?.length > 0 ? (
                                            paymentList.map((item, index) => (
                                                <div className="payment-list__item" key={index}>
                                                    <div className="payment-list__item-header">
                                                        <div className="payment-list__item-main">
                                                            <div className="payment-date">
                                                                <i className="fa-solid fa-calendar-days"></i> 
                                                                {format(new Date(item.paymentDate), 'MMM dd, yyyy')}
                                                            </div>
                                                            <div className="payment-amount">
                                                                <i className="fa-solid fa-peso-sign"></i>
                                                                <ToPeso amount={item.paidTotal} />
                                                            </div>
                                                        </div>
                                                        <div className="payment-list__item-actions">
                                                            <div className="payment-type">
                                                                <span className={`type-badge ${item.paymentType}`}>
                                                                    <i className={`fa-solid ${
                                                                        item.paymentType === 'cash' ? 'fa-money-bill' : 
                                                                        item.paymentType === 'cheque' ? 'fa-money-check' : 
                                                                        'fa-credit-card'
                                                                    }`}></i>
                                                                    {item.paymentType}
                                                                </span>
                                                            </div>
                                                            <Button 
                                                                variant="outline-danger" 
                                                                size="sm"
                                                                onClick={() => {
                                                                    handleShowDeletePayment();
                                                                    setPayment(item.id);
                                                                }}
                                                            >
                                                                <i className="fa-solid fa-trash"></i>
                                                            </Button>
                                                        </div>
                                                    </div>

                                                    <div className="payment-list__item-details">
                                                        <div className="payment-breakdown">
                                                            <h6>Payment Breakdown</h6>
                                                            <div className="breakdown-grid">
                                                                <div className="breakdown-item">
                                                                    <span className="label">Principal</span>
                                                                    <span className="amount"><ToPeso amount={item.paidPrincipal} /></span>
                                                                </div>
                                                                <div className="breakdown-item">
                                                                    <span className="label">Interest</span>
                                                                    <span className="amount"><ToPeso amount={item.paidInterest} /></span>
                                                                </div>
                                                                <div className="breakdown-item">
                                                                    <span className="label">Penalty</span>
                                                                    <span className="amount"><ToPeso amount={item.paidPenalty} /></span>
                                                                </div>
                                                                <div className="breakdown-item">
                                                                    <span className="label">Other</span>
                                                                    <span className="amount"><ToPeso amount={item.paidOther} /></span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {(item.prNumber || item.orNumber || item.paymentType === 'cheque') && (
                                                            <div className="payment-references">
                                                                <h6>Reference Numbers</h6>
                                                                <div className="reference-grid">
                                                                    {item.prNumber && (
                                                                        <div className="reference-item">
                                                                            <span className="label">PR#:</span>
                                                                            <span className="value">{item.prNumber}</span>
                                                                        </div>
                                                                    )}
                                                                    {item.orNumber && (
                                                                        <div className="reference-item">
                                                                            <span className="label">OR#:</span>
                                                                            <span className="value">{item.orNumber}</span>
                                                                        </div>
                                                                    )}
                                                                    {item.paymentType === 'cheque' && (
                                                                        <div className="reference-item">
                                                                            <span className="label">Check#:</span>
                                                                            <span className="value">{item.chequeNumber}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="no-payments-message">
                                                <div className="message-content">
                                                    <i className="fa-solid fa-receipt"></i>
                                                    <h6>No Payment Records</h6>
                                                    <p>No payments have been made for this loan yet.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    </>
                )}
            </div>
        </>
    );
}

export default LoanPage;
